(() => {
  if (window.__awFeaturesSliderLoaded) {
    return;
  }

  window.__awFeaturesSliderLoaded = true;

  const instances = new Map();
  const sliderUtils = window.AWSliderCoreUtils;
  const selectors = {
    root: "[data-aw-features-slider]",
    viewport: "[data-aw-features-slider-viewport]",
    list: "[data-aw-features-slider-list]",
    item: "[data-aw-features-slider-item]",
    scrollbar: "[data-aw-features-slider-scrollbar]",
    thumb: "[data-aw-features-slider-scrollbar-thumb]",
    prev: "[data-aw-features-slider-prev]",
    next: "[data-aw-features-slider-next]",
    videoTrigger: "[data-aw-features-slider-video-trigger]",
    videoFrame: "[data-aw-features-slider-video-frame]",
    videoTemplate: "[data-aw-features-slider-video-template]",
    modal: "[data-aw-features-slider-modal]",
    modalMedia: "[data-aw-features-slider-modal-media]",
    modalClose: "[data-aw-features-slider-modal-close]"
  };

  class AWFeaturesSlider {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector(selectors.viewport);
      this.list = root.querySelector(selectors.list);
      this.scrollbar = root.querySelector(selectors.scrollbar);
      this.thumb = root.querySelector(selectors.thumb);
      this.prevButton = root.querySelector(selectors.prev);
      this.nextButton = root.querySelector(selectors.next);
      this.videoTriggers = Array.from(root.querySelectorAll(selectors.videoTrigger));
      this.modal = root.querySelector(selectors.modal);
      this.modalMedia = root.querySelector(selectors.modalMedia);
      this.modalCloseButtons = Array.from(root.querySelectorAll(selectors.modalClose));
      this.rafId = null;
      this.teardownResizeObserver = null;
      this.lastTrigger = null;

      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onVideoTriggerClick = this.onVideoTriggerClick.bind(this);
      this.onModalCloseClick = this.onModalCloseClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onImageLoad = this.onImageLoad.bind(this);

      if (!this.viewport || !this.list || !this.scrollbar || !this.thumb) {
        return;
      }

      this.bindEvents();
      this.bindImageLoad();
      this.update();
    }

    isMobile() {
      return window.matchMedia("(max-width: 767px)").matches;
    }

    bindEvents() {
      if (this.prevButton) {
        this.prevButton.addEventListener("click", this.onPrevClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener("click", this.onNextClick);
      }

      this.viewport.addEventListener("scroll", this.onScroll, { passive: true });

      this.videoTriggers.forEach((trigger) => {
        trigger.addEventListener("click", this.onVideoTriggerClick);
      });

      this.modalCloseButtons.forEach((button) => {
        button.addEventListener("click", this.onModalCloseClick);
      });

      document.addEventListener("keydown", this.onKeyDown);

      this.teardownResizeObserver = sliderUtils.observeResize(
        [this.root, this.viewport, this.list],
        this.onResize
      );
    }

    bindImageLoad() {
      sliderUtils.bindImageLoad(this.root, this.onImageLoad);
    }

    onPrevClick() {
      if (this.prevButton && this.prevButton.getAttribute("aria-disabled") === "true") {
        return;
      }

      this.scroll(-1);
    }

    onNextClick() {
      if (this.nextButton && this.nextButton.getAttribute("aria-disabled") === "true") {
        return;
      }

      this.scroll(1);
    }

    onScroll() {
      if (this.rafId) {
        return;
      }

      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.update();
      });
    }

    onResize() {
      this.update();
    }

    onImageLoad() {
      this.update();
    }

    onVideoTriggerClick(event) {
      event.preventDefault();

      const trigger = event.currentTarget;
      const item = trigger.closest(selectors.item);
      const frame = item?.querySelector(selectors.videoFrame);
      const template = item?.querySelector(selectors.videoTemplate);

      if (!item || !frame || !template) {
        return;
      }

      this.playInline(item, frame, template);
    }

    onModalCloseClick(event) {
      event.preventDefault();
      this.closeModal();
    }

    onKeyDown(event) {
      if (event.key !== "Escape") {
        return;
      }

      if (!this.modal || this.modal.hidden) {
        return;
      }

      this.closeModal();
    }

    getItemDistance() {
      return sliderUtils.getItemDistance({
        list: this.list,
        itemSelector: selectors.item,
        viewport: this.viewport,
      });
    }

    scroll(direction) {
      this.viewport.scrollBy({
        left: direction * this.getItemDistance(),
        behavior: "smooth"
      });
    }

    updateButtonState(button, isDisabled) {
      sliderUtils.updateButtonState(button, isDisabled);
    }

    updateButtons() {
      const maxScroll = sliderUtils.getMaxScroll(this.viewport);
      const currentScroll = this.viewport.scrollLeft;
      const canScroll = maxScroll > 1;

      this.updateButtonState(this.prevButton, !canScroll || currentScroll <= 1);
      this.updateButtonState(this.nextButton, !canScroll || currentScroll >= maxScroll - 1);
    }

    updateScrollbar() {
      sliderUtils.updateScrollbar({
        viewport: this.viewport,
        scrollbar: this.scrollbar,
        thumb: this.thumb,
        enabled: sliderUtils.getMaxScroll(this.viewport) > 1,
      });
    }

    update() {
      this.updateButtons();
      this.updateScrollbar();
    }

    injectMedia(target, template) {
      target.innerHTML = "";
      target.appendChild(template.content.cloneNode(true));

      const video = target.querySelector("video");

      if (video) {
        const playPromise = video.play();

        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }
      }
    }

    playInline(item, frame, template) {
      if (item.classList.contains("is-playing")) {
        return;
      }

      item.classList.add("is-playing");
      this.injectMedia(frame, template);
      this.update();
    }

    openModal(template, trigger) {
      if (!this.modal || !this.modalMedia) {
        return;
      }

      this.lastTrigger = trigger;
      this.injectMedia(this.modalMedia, template);
      this.modal.hidden = false;
      this.modal.setAttribute("aria-hidden", "false");
      document.body.classList.add("is-aw-features-slider-modal-open");

      const closeButton = this.modal.querySelector(selectors.modalClose);

      if (closeButton) {
        closeButton.focus({ preventScroll: true });
      }
    }

    closeModal() {
      if (!this.modal || !this.modalMedia || this.modal.hidden) {
        return;
      }

      this.modal.hidden = true;
      this.modal.setAttribute("aria-hidden", "true");
      this.modalMedia.innerHTML = "";
      document.body.classList.remove("is-aw-features-slider-modal-open");

      if (this.lastTrigger) {
        this.lastTrigger.focus({ preventScroll: true });
      }
    }

    destroy() {
      if (this.prevButton) {
        this.prevButton.removeEventListener("click", this.onPrevClick);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener("click", this.onNextClick);
      }

      if (this.viewport) {
        this.viewport.removeEventListener("scroll", this.onScroll);
      }

      this.videoTriggers.forEach((trigger) => {
        trigger.removeEventListener("click", this.onVideoTriggerClick);
      });

      this.modalCloseButtons.forEach((button) => {
        button.removeEventListener("click", this.onModalCloseClick);
      });

      document.removeEventListener("keydown", this.onKeyDown);

      if (this.teardownResizeObserver) {
        this.teardownResizeObserver();
        this.teardownResizeObserver = null;
      }

      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      sliderUtils.unbindImageLoad(this.root, this.onImageLoad);

      this.closeModal();
    }
  }

  const init = (container = document) => {
    container.querySelectorAll(selectors.root).forEach((root) => {
      if (instances.has(root)) {
        instances.get(root).update();
        return;
      }

      instances.set(root, new AWFeaturesSlider(root));
    });
  };

  const destroy = (container) => {
    container.querySelectorAll(selectors.root).forEach((root) => {
      const instance = instances.get(root);

      if (!instance) {
        return;
      }

      instance.destroy();
      instances.delete(root);
    });
  };

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroy(event.target);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document));
  } else {
    init(document);
  }
})();
